/**
 * PauseButton renders the pause/resume toggle for timed games in the header.
 *
 * Why: Timed games need a way to pause/resume. This button provides that
 * control with consistent styling and focus navigation integration.
 *
 * When to use: Rendered by Header when the active game has timers and
 * the game is not over.
 */
import * as React from 'react'
import { cn } from '@merels/ui'
import { IconButton } from '../../library/IconButton/IconButton'

export interface PauseButtonProps {
  /** Whether the game is currently paused */
  isPaused: boolean
  /** Callback to toggle pause state */
  onToggle: () => void
  /** Ref for focus management (callback or object ref) */
  buttonRef?: React.Ref<HTMLButtonElement>
  /** Tab index for focus navigation */
  tabIndex?: number
  /** Whether this button is currently focused */
  isFocused?: boolean
  /** Accent color for styling */
  accentColor?: string
  /** Ink when filled with accentColor (hover) */
  accentInkColor?: string
  /** Background color for focus ring */
  backgroundColor?: string
}

export function PauseButton({
  isPaused,
  onToggle,
  buttonRef,
  tabIndex,
  isFocused,
  accentColor,
  accentInkColor = '#ffffff',
  backgroundColor,
}: PauseButtonProps) {
  const label = isPaused ? "Resume game" : "Pause game"

  return (
    <IconButton
      ref={buttonRef}
      onClick={onToggle}
      aria-label={label}
      title={label}
      tabIndex={tabIndex}
      className={cn(
        "flex h-[var(--menu-btn-size)] w-[var(--menu-btn-size)] items-center justify-center rounded-lg transition-colors",
        isFocused && "outline-none"
      )}
      style={{
        ...(accentColor ? { color: accentColor } : {}),
        ...(isFocused && accentColor ? {
          boxShadow: `0 0 0 2px ${accentColor}, 0 0 0 4px ${backgroundColor}`,
        } : {}),
      }}
      onMouseEnter={(e) => {
        if (accentColor) {
          e.currentTarget.style.backgroundColor = accentColor
          e.currentTarget.style.color = accentInkColor
        }
      }}
      onMouseLeave={(e) => {
        if (accentColor) {
          e.currentTarget.style.backgroundColor = ''
          e.currentTarget.style.color = accentColor
        }
      }}
      icon={
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          {isPaused ? (
            <polygon points="5 3 19 12 5 21 5 3" />
          ) : (
            <>
              <rect x="6" y="4" width="4" height="16" />
              <rect x="14" y="4" width="4" height="16" />
            </>
          )}
        </svg>
      }
    />
  )
}
